import { useCallback, useEffect, useEffectEvent, useRef, useState } from 'react';
import { EVENTS } from 'shared/constants';
import uploadService from 'shared/services/upload.service';
import { FileWithPreview } from 'src/components/core';
import analyticsService from 'src/helpers/analytics.service';
import { useUploadProgress } from './upload-progress.hooks';
import { useConversion } from '../../smart-format-conversion.context';
import { isSpreadsheetFile } from 'shared/utils';
import { useIsDocumentEditorSupported } from 'src/components/document-editor';

type Asset = {
  assetId: string;
  type: string;
  url: string;
};

type Duplication = {
  knowledgeId: string;
};

const useUploadLogic = ({
  convertPastItemToSmartFormat = false,
  editExternal,
  close,
  props,
  isCancelDialogOpen = false,
}: {
  convertPastItemToSmartFormat?: boolean;
  editExternal: (arg: {
    id: string;
    categoryId: string;
    ownerIds: string[];
    title: string;
    link: string;
    type: string;
    fileName: string;
  }) => void;
  close: () => void;
  props: any; // previously saved knowledge item
  isCancelDialogOpen?: boolean;
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [failed, setFailed] = useState<boolean>(false);
  const [failureReason, setFailureReason] = useState<string | null>(null);
  const asset = useRef<Asset | undefined>(undefined);
  const { startConversion } = useConversion();
  const [file, setFile] = useState<FileWithPreview | null>(null);

  const [smartFormat, setSmartFormat] = useState<boolean>(convertPastItemToSmartFormat);
  const isDocumentEditorSupported = useIsDocumentEditorSupported();
  const [duplication, setDuplication] = useState<Duplication | null>(null);
  const cancelled = useRef(false);
  const isCancelDialogOpenRef = useRef(isCancelDialogOpen);
  const pausedRef = useRef(false);
  const prevCancelDialogOpenRef = useRef(isCancelDialogOpen);
  const pendingDuplicateRef = useRef<Duplication | null>(null);

  // Keep ref in sync with prop - update synchronously during render
  isCancelDialogOpenRef.current = isCancelDialogOpen;

  const { progress, status, onUploadProgress, completeProgress } = useUploadProgress({ file });

  const reset = useCallback(() => {
    setDuplication(null);
    setFailed(false);
    setFailureReason(null);
    setFile(null);
    setLoading(false);
    asset.current = undefined;
    cancelled.current = false;
  }, []);

  /*
    ---
    FILE UPLOAD
    ---
  */

  /*
    Start upload process
  */
  const uploadFile = useCallback(
    async ({
      newFile,
      skipOpenEditor = false,
    }: {
      newFile?: FileWithPreview;
      skipOpenEditor?: boolean;
    } = {}) => {
      cancelled.current = false;
      setLoading(true);
      setFailureReason(null);

      // Spreadsheets stay in their original format only when the Document Editor
      // can render them; otherwise convert to PDF so a preview is available
      const fileName = newFile?.name ?? file?.name;
      const skipPdfConversion = isDocumentEditorSupported && isSpreadsheetFile(fileName);

      analyticsService.track(EVENTS.KNOWLEDGE_ITEMS.UPLOAD_FILE_START, {
        skip_pdf_conversion: skipPdfConversion,
      });

      // Upload the file
      const uploadFileRes = (await uploadService.uploadFile({
        file: newFile ?? file,
        onUploadProgress,
      })) as
        | {
            assetId: string;
            url: string;
            type: string;
            error?: undefined;
          }
        | {
            error: string;
          };

      if (uploadFileRes?.error != null) {
        setFailed(true);
        setLoading(false);
        return { error: uploadFileRes?.error };
      }

      // Set the asset
      asset.current = uploadFileRes;

      // finalize upload
      const finalizeRes = await uploadService.finalizeUploadFile({
        assetId: uploadFileRes?.assetId,
      });

      if (finalizeRes?.error) {
        setFailed(true);
        setLoading(false);
        return { error: finalizeRes?.error };
      }

      // Check for duplicates
      if (finalizeRes?.duplicate) {
        // Don't proceed if the cancel dialog is open - user might cancel
        if (isCancelDialogOpenRef.current) {
          pausedRef.current = true;
          pendingDuplicateRef.current = finalizeRes.duplicate;
          return { asset: asset.current, cancelled: cancelled.current, paused: true };
        }

        setLoading(false);
        analyticsService.track(EVENTS.KNOWLEDGE_ITEMS.DUPLICATION_DETECTED);
        // show message with the duplicate knowledge item
        setDuplication(finalizeRes?.duplicate);
        return { error: 'duplicated-file', duplicate: finalizeRes?.duplicate };
      }

      if (!skipPdfConversion) {
        // Convert to pdf
        const pdfAsset = await uploadService.convertAssetToPdf({ assetId: uploadFileRes?.assetId });

        if (pdfAsset?.error) {
          setFailed(true);
          setFailureReason(pdfAsset.error);
          setLoading(false);
          return { error: pdfAsset?.error };
        }

        // asset was converted to pdf
        if (pdfAsset) {
          asset.current = {
            ...asset.current,
            url: pdfAsset?.convertedFilePath,
            type: pdfAsset?.newFormat,
          };
        }
      }

      // Don't proceed if the cancel dialog is open - user might cancel
      if (isCancelDialogOpenRef.current) {
        pausedRef.current = true;
        return { asset: asset.current, cancelled: cancelled.current, paused: true };
      }

      pausedRef.current = false;

      if (!smartFormat && !skipOpenEditor) {
        const fileName = file?.name || props?.fileName;
        editExternal({
          id: props?.id,
          categoryId: props?.categoryId,
          ownerIds: props?.ownerIds,
          title: props?.title || '',
          link: asset.current!.url,
          type: asset.current!.type,
          fileName,
        });
        close();
      }

      return { asset: asset.current, cancelled: cancelled.current };
    },
    [file, onUploadProgress, smartFormat, props, editExternal, close, isDocumentEditorSupported],
  );

  /*
    ---
    SMART FORMAT CONVERSION
    ---
  */

  const convertToSmartFormat = useCallback(async () => {
    cancelled.current = false;
    setLoading(true);
    analyticsService.track(EVENTS.SMART_FORMAT_CONVERSION.START, {
      conversionTime: EVENTS.SMART_FORMAT_CONVERSION_TIME.IMMEDIATE,
    });

    const { error, paused } = await uploadFile();

    if (error) {
      setLoading(false);
      setFailed(true);
      return;
    }

    // Don't proceed if paused due to cancel dialog being open
    if (paused) {
      return;
    }

    startConversion(
      { assetId: asset.current!.assetId, url: asset.current!.url },
      {
        ...props,
        knowledgeId: props?.id,
        knowledgeTitle: props?.title,
        fileName: file?.name || props?.fileName,
        onRetry: () => {
          setFailed(false);
          setLoading(true);
        },
      },
    );

    close();
  }, [file, props, startConversion, close, uploadFile]);

  /*
    If knowledge item already exists, start smart conversion right away
  */
  const cancelOnDuplication = async () => {
    setLoading(true);
    analyticsService.track(EVENTS.KNOWLEDGE_ITEMS.DUPLICATION_DETECTED_DISMISS);
    // Reject the duplicated item - notify the server to remove
    await uploadService.rejectDuplicatedItem({ assetId: asset.current?.assetId });

    // Reset the state
    setFailed(false);
    asset.current = undefined;
    setLoading(false);
    close();
  };

  const cancelProgress = () => {
    close();
    cancelled.current = true;
  };

  const cancelUpload = () => {
    setLoading(false);
    setFailed(true);
    cancelled.current = true;
  };

  const retryUpload = () => {
    setFailed(false);
    setFailureReason(null);
    setFile(null);
  };

  const uploadFileHandler = (name?: string, file?: FileWithPreview | '') => {
    setFile(file && typeof file !== 'string' ? file : null);
  };

  const toggleSmartFormat = (value: boolean) => {
    analyticsService.track(EVENTS.SMART_FORMAT_CONVERSION.TOGGLE_STATE, {
      state: value,
    });
    setSmartFormat(value);
  };

  // Convert an existing item to smart format
  const convertExistingItem = useCallback(async () => {
    if (!props?.id || loading || failed) return;

    setLoading(true);
    cancelled.current = false;

    analyticsService.track(EVENTS.SMART_FORMAT_CONVERSION.START, {
      id: props.id,
      title: props.title,
      conversionTime: EVENTS.SMART_FORMAT_CONVERSION_TIME.LATER,
    });

    try {
      // Trigger OCR
      const newAsset = await uploadService.retroOcrConversion({ itemId: props.id });
      asset.current = newAsset;

      startConversion(
        { assetId: asset.current.assetId, url: asset.current.url },
        {
          ...props,
          knowledgeId: props.id,
          knowledgeTitle: props.title,
          fileName: props.fileName,
          onRetry: () => {
            setFailed(false);
            setLoading(true);
            convertExistingItem();
          },
        },
      );

      close();
    } catch (error) {
      setFailed(true);
      setLoading(false);
    }
  }, [props, loading, failed, cancelled, asset, startConversion, close]);

  // useEffectEvent gives us the latest convertExistingItem on every call
  // without making it a reactive dependency of the effect below.
  const onConvertExistingItem = useEffectEvent(() => {
    convertExistingItem();
  });

  // Start conversion if convertPastItemToSmartFormat is true.
  // Only re-runs when the prop changes, not every time convertExistingItem
  // is recreated (which would cause duplicate OCR tasks).
  useEffect(() => {
    if (convertPastItemToSmartFormat) {
      onConvertExistingItem();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convertPastItemToSmartFormat]);

  // Resume upload when cancel dialog closes without confirming
  useEffect(() => {
    const wasPreviouslyOpen = prevCancelDialogOpenRef.current;
    prevCancelDialogOpenRef.current = isCancelDialogOpen;

    // Dialog was open and now closed, and we were paused, and not cancelled
    if (wasPreviouslyOpen && !isCancelDialogOpen && pausedRef.current && !cancelled.current) {
      pausedRef.current = false;

      // Handle pending duplicate detection
      if (pendingDuplicateRef.current) {
        setLoading(false);
        analyticsService.track(EVENTS.KNOWLEDGE_ITEMS.DUPLICATION_DETECTED);
        setDuplication(pendingDuplicateRef.current);
        pendingDuplicateRef.current = null;
        return;
      }

      // Continue with the upload flow
      if (smartFormat) {
        // Resume smart format conversion
        startConversion(
          { assetId: asset.current!.assetId, url: asset.current!.url },
          {
            ...props,
            knowledgeId: props?.id,
            knowledgeTitle: props?.title,
            fileName: file?.name || props?.fileName,
            onRetry: () => {
              setFailed(false);
              setLoading(true);
              convertToSmartFormat();
            },
          },
        );
        close();
      } else {
        const fileName = file?.name || props?.fileName;
        editExternal({
          id: props?.id,
          categoryId: props?.categoryId,
          ownerIds: props?.ownerIds,
          title: props?.title || fileName || '',
          link: asset.current!.url,
          type: asset.current!.type,
          fileName,
        });
        close();
      }
    }
  }, [
    isCancelDialogOpen,
    smartFormat,
    file,
    props,
    editExternal,
    close,
    startConversion,
    convertToSmartFormat,
  ]);

  return {
    loading,
    failed,
    failureReason,
    duplication,
    file,
    asset: asset.current,
    progress,
    status,
    isSmartFormat: smartFormat,
    cancelled: cancelled.current,
    toggleSmartFormat,
    uploadFile,
    convertToSmartFormat,
    cancelOnDuplication,
    cancelProgress,
    retryUpload,
    uploadFileHandler,
    cancelUpload,
    completeProgress,
    reset,
  };
};

export { useUploadLogic };
